import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, FormGroup, Label, Input, Button, Row, Col, Alert, Spinner } from 'reactstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import { useRoleBasedData } from '../../../hooks/useRoleBasedData';
import { getClubInfo, updateClubInfo, UpdateClubPayload } from '../../../api/club/clubService';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import secureApi from '../../../utils/secureApi';
import imageCompression from 'browser-image-compression';
import './ClubInformation.css';
import { getImageUrl } from '../../../utils/imageUrl';

const ClubInformation = () => {
  const navigate = useNavigate();
  const { getAccessibleClubIds } = useRoleBasedData();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  
  // Memoize club IDs to prevent unnecessary re-renders
  const clubIds = useMemo(() => getAccessibleClubIds(), [getAccessibleClubIds]);
  
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    emailId: '',
    managerName: '',
    countryCode: 91,
    managerContactNum: '',
    location: '',
    isActive: true,
    // ✅ NEW FIELDS
    address: '',
    gstNumber: ''
  });
  
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [clubId, setClubId] = useState<string>('');
  
  // Track original form data for dirty checking
  const [originalFormData, setOriginalFormData] = useState(formData);

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return formData.name.trim() !== '' && 
           formData.managerName.trim() !== '' &&
           formData.location.trim() !== '' &&
           formData.address.trim() !== '';
  }, [formData]);

  // Check if form is dirty (has changes) - exclude email since it's read-only
  const isFormDirty = useMemo(() => {
    const currentData = { ...formData };
    const originalData = { ...originalFormData };
    
    // Remove emailId from comparison since it's read-only
    const { emailId: _, ...currentWithoutEmail } = currentData;
    const { emailId: __, ...originalWithoutEmail } = originalData;
    
    return JSON.stringify(currentWithoutEmail) !== JSON.stringify(originalWithoutEmail);
  }, [formData, originalFormData]);

  // Contact number validation
  const validateContactNumber = (number: string): boolean => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return number === '' || phoneRegex.test(number.replace(/\s/g, ''));
  };

  // Logo upload function using presigned URL
  const handleLogoUpload = async (file: File) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoError('Image size should be less than 5MB.');
      return;
    }

    setLogoError(null);
    setIsUploading(true);
    setUploadProgress(0);
    setUploadSuccess(false);

    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        onProgress: (progress) => setUploadProgress(progress)
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `club_logo_${Date.now()}.${ext}`;
      const context = `my-club/club-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data with new logo
      setFormData(prev => ({ ...prev, logo: s3Key }));
      setLogoPreview(getImageUrl(s3Key));
      setUploadSuccess(true);
      
    } catch (err: any) {
      setLogoError('Logo upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const fetchClubInfo = useCallback(async () => {
    // Prevent multiple simultaneous API calls
    if (isFetchingRef.current || dataLoaded) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      if (clubIds.length === 0) {
        setError('No club access found for this user. Please contact your administrator.');
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Use the first club ID from user's permissions
      const clubId = clubIds[0];
      setClubId(clubId);
      
      const clubInfo = await getClubInfo(clubId);
      
      // Update form data with fetched club information
      const newFormData = {
        name: clubInfo.name || '',
        logo: clubInfo.logo || '',
        emailId: clubInfo.emailId || '',
        managerName: clubInfo.managerName || '',
        countryCode: clubInfo.countryCode || 91,
        managerContactNum: clubInfo.managerContactNum?.toString() || '',
        location: clubInfo.location || '',
        isActive: clubInfo.isActive !== undefined ? clubInfo.isActive : true,
        // ✅ NEW FIELDS
        address: clubInfo.address || '',
        gstNumber: clubInfo.gstNum || ''  // Map gstNum from API to gstNumber in form
      };
      
      setFormData(newFormData);
      setOriginalFormData(newFormData); // Set original data for dirty checking

      // Set logo preview if available
      if (clubInfo.logo) {
        const logoUrl = getImageUrl(clubInfo.logo);
        setLogoPreview(logoUrl);
      }
      
      setDataLoaded(true);
      
    } catch (err: any) {
      setError(`Failed to load club information: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [clubIds, dataLoaded]);

  // Fetch club information when club IDs are available
  useEffect(() => {
    if (!dataLoaded && !isFetchingRef.current && clubIds.length > 0) {
      fetchClubInfo();
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      isFetchingRef.current = false;
    };
  }, [clubIds, dataLoaded, fetchClubInfo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Prevent editing of emailId field
    if (name === 'emailId') {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveClubDetails = async () => {
    if (!isFormValid || !clubId || !isFormDirty) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Prepare data for API
      const updateData: UpdateClubPayload = {
        name: formData.name,
        logo: formData.logo,
        emailId: formData.emailId,
        managerName: formData.managerName,
        countryCode: formData.countryCode,
        managerContactNum: parseInt(formData.managerContactNum) || 0,
        location: formData.location,
        isActive: formData.isActive,
        // ✅ NEW FIELDS
        address: formData.address,
        gstNum: formData.gstNumber  // Map gstNumber to gstNum for API
      };

      // Update club information
      const updatedClubInfo = await updateClubInfo(clubId, updateData);
      
      setSuccess('Club information updated successfully!');
      
      // Update form data with response
      const newFormData = {
        name: updatedClubInfo.name || formData.name,
        logo: updatedClubInfo.logo || formData.logo,
        emailId: updatedClubInfo.emailId || formData.emailId,
        managerName: updatedClubInfo.managerName || formData.managerName,
        countryCode: updatedClubInfo.countryCode || formData.countryCode,
        managerContactNum: updatedClubInfo.managerContactNum?.toString() || formData.managerContactNum,
        location: updatedClubInfo.location || formData.location,
        isActive: updatedClubInfo.isActive !== undefined ? updatedClubInfo.isActive : formData.isActive,
        // ✅ NEW FIELDS
        address: updatedClubInfo.address || formData.address,
        gstNumber: updatedClubInfo.gstNum || formData.gstNumber  // Map gstNum from API to gstNumber in form
      };
      
      setFormData(newFormData);
      setOriginalFormData(newFormData); // Update original data after successful save

    } catch (err: any) {
      setError('Failed to update club information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid" style={{ marginTop: '20px' }}>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs
            items={[
              { label: 'My Club' },
              { label: 'Club Information', active: true }
            ]}
          />
        </div>
        
        <div className="mb-4">
          <h4 className="page-title">Club Master Profile</h4>
        </div>

        {/* Shimmer loader for club details */}
        <Row>
          <Col lg={12}>
            <Card className="mb-4 club-card">
              <CardBody>
                <h5 className="card-title">Club Details</h5>
                <ShimmerLoader type="form" />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ marginTop: '20px' }}>
      {/* Breadcrumbs */}
      <div className="mb-3" style={{ padding: '0 15px' }}>
        <Breadcrumbs
          items={[
            { label: 'My Club' },
            { label: 'Club Information', active: true }
          ]}
        />
      </div>
      
      <div className="mb-4">
        <h4 className="page-title">Club Master Profile</h4>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert color="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert color="success" className="mb-4">
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          {/* Club Details Section */}
          <Card className="mb-4 club-card">
            <CardBody>
              <h5 className="card-title">Club Details</h5>
              
              {/* First Row - Logo Upload Only */}
              <Row className="mb-4">
                <Col md={12}>
                  <FormGroup className="logo-upload-group">
                    <Label for="logo" className="form-label">
                      Upload Club Logo
                    </Label>
                    <div className="club-logo-upload">
                      <div 
                        className="logo-preview"
                        style={{
                          background: logoPreview ? `url(${logoPreview}) center/cover` : '#f8f9fa'
                        }}
                        onClick={() => document.getElementById('logo-upload')?.click()}
                      >
                        {!logoPreview && (
                          <div className="upload-placeholder">
                            <i className="ph-image"></i>
                            <div className="small">Upload Logo</div>
                          </div>
                        )}
                        {isUploading && (
                          <div className="upload-overlay">
                            <Spinner size="sm" />
                            <div className="small mt-2">Uploading... {uploadProgress}%</div>
                          </div>
                        )}
                      </div>
                      <div 
                        className="edit-icon"
                        onClick={() => document.getElementById('logo-upload')?.click()}
                      >
                        <i className="ph-pencil-simple"></i>
                      </div>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files?.[0] && handleLogoUpload(e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {logoError && (
                      <div className="text-danger small mt-2">{logoError}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              
              {/* Second Row - All Other Fields */}
              <Row>
                <Col md={4}>
                  <FormGroup className="form-field-group">
                    <Label for="name" className="form-label required-field">
                      Club Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter Club Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </FormGroup>
                </Col>
                
                <Col md={4}>
                  <FormGroup className="form-field-group">
                    <Label for="managerName" className="form-label required-field">
                      Team/Club Manager Name
                    </Label>
                    <Input
                      id="managerName"
                      name="managerName"
                      type="text"
                      placeholder="Enter Team/Club Manager Name"
                      value={formData.managerName}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </FormGroup>
                </Col>
                
                <Col md={4}>
                  <FormGroup className="form-field-group">
                    <Label for="managerContactNum" className="form-label">
                      Contact Number
                    </Label>
                    <Input
                      id="managerContactNum"
                      name="managerContactNum"
                      type="tel"
                      placeholder="Enter Contact Number"
                      value={formData.managerContactNum}
                      onChange={handleInputChange}
                      className={`form-control ${formData.managerContactNum && !validateContactNumber(formData.managerContactNum) ? 'is-invalid' : ''}`}
                    />
                    {formData.managerContactNum && !validateContactNumber(formData.managerContactNum) && (
                      <div className="invalid-feedback">
                        Please enter a valid contact number (10-15 digits)
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <FormGroup className="form-field-group">
                    <Label for="emailId" className="form-label">
                      Email (Read Only)
                    </Label>
                    <Input
                      id="emailId"
                      name="emailId"
                      type="email"
                      placeholder="Email address"
                      value={formData.emailId}
                      onChange={handleInputChange}
                      className="form-control"
                      readOnly
                      disabled
                    />
                  </FormGroup>
                </Col>
                
                <Col md={6}>
                  <FormGroup className="form-field-group">
                    <Label for="location" className="form-label required-field">
                      Location
                    </Label>
                    <Input
                      id="location"
                      name="location"
                      type="text"
                      placeholder="Enter Location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </FormGroup>
                </Col>
              </Row>
              
              {/* ✅ NEW ROW: Address and GST Number */}
              <Row>
                <Col md={6}>
                  <FormGroup className="form-field-group">
                    <Label for="address" className="form-label required-field">
                      Address
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      type="textarea"
                      placeholder="Enter Club Address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="form-control"
                      rows={3}
                    />
                  </FormGroup>
                </Col>
                
                <Col md={6}>
                  <FormGroup className="form-field-group">
                    <Label for="gstNumber" className="form-label">
                      GST Number
                    </Label>
                    <Input
                      id="gstNumber"
                      name="gstNumber"
                      type="text"
                      placeholder="Enter GST Number (Optional)"
                      value={formData.gstNumber}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Save Button */}
          <div className="d-flex justify-content-end">
            <Button 
              color="primary" 
              size="lg"
              className={`save-btn ${!isFormValid || !isFormDirty ? 'disabled' : ''}`}
              onClick={handleSaveClubDetails}
              disabled={!isFormValid || !isFormDirty || saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Club Details'
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ClubInformation; 